import React, { Suspense, useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { useGLTF, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import SylentsAnimation from '../components/SylentsAnimation'; // Adjust path if needed

// --- Preload Model (Good Practice) ---
// useGLTF.preload(modelPath); // Call this where you use SceneViewer if path is dynamic

// --- Helper for Mesh Grouping (Simplified from App.jsx) ---
const processMeshGroups = (scene) => {
  console.log("SceneViewer: Processing scene graph...");
  const groups = {};
  if (!scene) return groups;
  scene.traverse((child) => {
    if (child.isMesh) {
      if (child.material) {
        // Ensure material cloning doesn't cause issues; might need deeper inspection
        // depending on material types and shared resources.
        // child.material = child.material.clone(); 
        // child.material.transparent = true; // Setting transparency might not always be desired
        // child.material.needsUpdate = true;
      }
      child.castShadow = true;
      child.receiveShadow = true;
      const prefix = child.name.split('_')[0];
      if (prefix) {
        if (!groups[prefix]) { groups[prefix] = []; }
        groups[prefix].push(child);
      }
    }
  });
  console.log("SceneViewer: Final Mesh Groups:", groups);
  return groups;
};

const SceneViewer = ({ sceneConfig, modelPath = '/models/model.glb' }) => {
  // Mobile detection
  const isMobile = useMemo(() => {
    if (typeof window !== 'undefined') {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    }
    return false;
  }, []);

  // --- Choose appropriate model based on device ---
  // Fix: check if path already includes _mobile before appending it
  const mobilePath = modelPath.includes('_mobile.glb') 
    ? modelPath 
    : modelPath.replace('.glb', '_mobile.glb');
  const effectiveModelPath = isMobile ? mobilePath : modelPath;
  
  // Log which model is being used
  useEffect(() => {
    console.log(`SceneViewer: Using ${isMobile ? 'mobile' : 'desktop'} model: ${effectiveModelPath}`);
  }, [isMobile, effectiveModelPath]);

  const { scene } = useGLTF(effectiveModelPath);
  const orbitControlsRef = useRef();
  const cameraRef = useRef(); // Ref for the camera component
  const scenePlayerRef = useRef(); // Ref passed to SylentsAnimation

  // Process meshes once model is loaded
  const meshGroups = useMemo(() => processMeshGroups(scene), [scene]);

  // Determine initial visibility (assuming all visible except logo)
  const initialVisibility = useMemo(() => {
    return Object.keys(meshGroups).reduce((acc, groupName) => {
      acc[groupName] = groupName !== 'logo'; // Example: hide logo initially
      return acc;
    }, {});
  }, [meshGroups]);
  
   // --- Handle Camera State ---
   useEffect(() => {
      if (cameraRef.current && orbitControlsRef.current && sceneConfig?.cameraState) {
          try {
              console.log("SceneViewer: Setting initial camera state:", sceneConfig.cameraState);
              // Set camera position using API
              cameraRef.current.position.set(...sceneConfig.cameraState.position);
              
              // Set OrbitControls target
              orbitControlsRef.current.target.set(...sceneConfig.cameraState.target);
              
              // Update OrbitControls after changing target/position
              orbitControlsRef.current.update(); 
          } catch (e) {
              console.error("SceneViewer: Error setting initial camera state:", e);
          }
      }
   }, [sceneConfig, cameraRef, orbitControlsRef]); // Depend on config and refs

  // --- Simplified Lighting/Defaults ---
  const ambientIntensity = 1;
  const dirIntensity = 10;
  const dirPosition = [10, 10, 5]; // Default Three.js directional light position
  const dirColor = '#ffffff';
  const fillIntensity = 0;
  const fillPosition = [-10, 5, -5];
  const fillColor = '#ffffff';
  const modelPosition = [0, 0, 0]; // Default model position in viewer

  // --- Water/Particle Settings (from config if water scene) ---
  const waterSettings = useMemo(() => {
      if (sceneConfig?.isWaterScene && sceneConfig?.waterSettings) {
          console.log("SceneViewer: Using water settings from scene config:", sceneConfig.waterSettings);
          // Ensure waterEnabled is explicitly set based on isWaterScene
          return { ...sceneConfig.waterSettings, waterEnabled: true }; 
      }
      // Provide default fallback values if not a water scene or settings are missing
      console.log("SceneViewer: Using default water settings (water disabled).");
      return { 
          waterEnabled: false, waterColor: '#5caddd', lineWidth: 2, // Ensure waterEnabled is false
          particleColor: '#88ccff', particleSize: 1.0, particleDensity: 1.0, 
          particleIntakeYOffset: -0.2, waveHeight: 0.08, waveSpeed: 1.5, waveFrequency: 0.3 
      };
  }, [sceneConfig]);

  // Callback needed by SylentsAnimation (can be simplified)
  const handleScenePlayerReady = useCallback(() => {
    console.log("SceneViewer: SylentsAnimation reported ready.");
    // Auto-play keyframe animation if applicable
    if (scenePlayerRef.current && sceneConfig && !sceneConfig.isWaterScene && sceneConfig.keyframeAnimations && Object.keys(sceneConfig.keyframeAnimations).length > 0) {
         console.log("SceneViewer: Auto-playing keyframe animation for scene:", sceneConfig.label);
         scenePlayerRef.current.playSceneAnimation(sceneConfig); // Assuming SylentsAnimation exposes this
    } else if (sceneConfig?.isWaterScene) {
         console.log("SceneViewer: Water scene detected, no automatic keyframe playback.");
    } else {
         console.log("SceneViewer: No keyframes found or not ready for auto-play.");
    }
  }, [sceneConfig]); // Depend on sceneConfig

  // --- Render Logic ---
  if (!sceneConfig) {
      // Optionally return a specific loader for config
      return <div>Loading Scene Configuration...</div>;
  }
    
  // Don't render Canvas until model is loaded and meshes processed
  if (Object.keys(meshGroups).length === 0) {
      // Optionally return a specific loader for model
      return <div>Loading Model & Processing Meshes...</div>;
  }

  // Default camera position if not set by config
  const initialCameraPos = sceneConfig?.cameraState?.position || [0, 2, 10];

  return (
    // Canvas takes full size of parent container (which needs dimensions)
    <Canvas shadows camera={{ position: initialCameraPos, fov: 50, ref: cameraRef }}> 
      <Suspense fallback={null}> 
        {/* SylentsAnimation receives all necessary props */}
        <SylentsAnimation
          scene={scene} // The loaded GLTF scene graph
          meshGroups={meshGroups} // Processed groups of meshes
          visibilityState={initialVisibility} // Initial visibility map
          orbitControlsRef={orbitControlsRef} // Ref for controls
          scenePlayerRef={scenePlayerRef} // Ref for animation playback control
          activeSceneId={sceneConfig.id} // ID of the current scene
          sceneConfigForPlayback={sceneConfig} // Full config for internal playback logic
          ambientIntensity={ambientIntensity}
          dirIntensity={dirIntensity}
          dirPosition={dirPosition}
          dirColor={dirColor}
          fillIntensity={fillIntensity}
          fillPosition={fillPosition}
          fillColor={fillColor}
          cameraRef={cameraRef} // Pass camera ref down
          onReady={handleScenePlayerReady} // Callback when ready
          isTimelineReady={true} // Simplified: assume ready
          modelPosition={modelPosition} // Base position for the model within the viewer
          // Pass water/particle settings derived from useMemo
          waterEnabled={waterSettings.waterEnabled}
          waterColor={waterSettings.waterColor}
          lineWidth={waterSettings.lineWidth}
          waveHeight={waterSettings.waveHeight}
          waveSpeed={waterSettings.waveSpeed}
          waveFrequency={waterSettings.waveFrequency}
          // Pass particle props explicitly (relying on WaterParticles accepting props)
          particleColor={waterSettings.particleColor}
          particleSize={waterSettings.particleSize}
          particleDensity={waterSettings.particleDensity}
          particleIntakeYOffset={waterSettings.particleIntakeYOffset}
        />
      </Suspense>
      {/* OrbitControls setup - disable user interaction */}
      <OrbitControls ref={orbitControlsRef} enabled={false} makeDefault />
    </Canvas>
  );
};

export default SceneViewer; 